/**
 * What a Voice SDK error MEANS for the call the operator is looking at.
 *
 * React-free and in its own file because the answer depends on two things that
 * are impossible to test through the window: the code Twilio sent, and the
 * phase the call was in when it arrived. The same 31005 is a broken setup while
 * the leg is still being built and a perfectly ordinary goodbye once the two of
 * them are talking.
 *
 * ⚠ THE BUG THIS EXISTS TO PREVENT, reported from production on 2026-08-12: the
 * customer put the phone down and the call window went red with "Twilio refused
 * the call, error 31005. Check the telephony settings" - on a call that had just
 * ended normally, with nothing wrong with the settings at all. `Call._onHangup`
 * in the Voice SDK emits `error` whenever the gateway's HANGUP carries an error
 * payload and calls `_disconnect()` on the very next line, so the window painted
 * a configuration failure over the end of a healthy conversation. The
 * `disconnect` handler then keeps an error phase on purpose, so the red screen
 * was the last word: no duration, no redial button, no after-call note box.
 *
 * This file also carries the two helpers that turn `CallState.error` into a
 * message, so everything that decides what an error is called lives together.
 */
import { ApiError } from "../../lib/api";
import type { CallDirection, CallPhase } from "./CallProvider";

/**
 * What the window should do about an error the SDK raised on a live `Call`.
 *
 *  - `ignore`  the call is already over; there is nothing left to repaint.
 *  - `ended`   the far end hung up. Not an error at all, in operator terms.
 *  - `dropped` the line died under a live conversation. Worth saying, but it is
 *              not a refusal and the telephony settings are not the answer.
 *  - `failed`  the call never got up. This is the one the settings can fix.
 */
export type CallErrorVerdict = "ignore" | "ended" | "dropped" | "failed";

/**
 * `GeneralErrors.ConnectionError`, and the code behind the reported bug.
 *
 * The SDK raises it in exactly two places (`twilio/call.ts`), and both mean the
 * call is over rather than refused: a HANGUP from the gateway that carries an
 * error payload, and a DTMF digit that cannot be sent because signalling is
 * already gone.
 *
 * ⚠ It is also the SDK's CATCH-ALL for gateway errors. With
 * `enableImprovedSignalingErrorPrecision` off - the default, and what this app
 * builds its `Device` with - every code Twilio has no precise class for arrives
 * as this one, with the real number tucked inside `originalError`. So 31005
 * says "the gateway ended the call and had something to say about it" and
 * nothing more precise than that. The payload itself is not lost: the SDK logs
 * it ("Received an error from the gateway"), and `logLevel: "error"` in
 * `device/useVoiceDevice.ts` is exactly the level that prints it.
 */
const GATEWAY_HANGUP = 31005;

/** Phases in which this window has nothing left to say about a call. */
const OVER: ReadonlySet<CallPhase> = new Set<CallPhase>(["idle", "ended", "error"]);

/**
 * Is there a leg at the FAR END right now?
 *
 * The whole classification turns on this. An error before there is one is a
 * call that could not be placed; an error after it is a call that stopped.
 *
 * The two directions disagree about `connecting`, which is why the direction is
 * a parameter rather than something this could work out on its own. Outbound,
 * `connecting` is `device.connect()` in flight and nothing has rung anywhere.
 * Inbound, it is the gap between the operator pressing Answer and the SDK
 * reporting the call as accepted - the caller has been on the line the whole
 * time, and their hanging up in that gap is an ended call, not a failed one.
 */
function farEndIsUp(phase: CallPhase, direction: CallDirection): boolean {
  if (phase === "ringing" || phase === "active") return true;
  return direction === "inbound" && phase === "connecting";
}

/**
 * Classify an error from `call.on("error")`.
 *
 * `code` is `err.code` as the SDK hands it over; it is optional because an
 * error without one is possible and still has to land somewhere.
 */
export function classifyCallError(
  code: number | undefined,
  phase: CallPhase,
  direction: CallDirection,
): CallErrorVerdict {
  // The guard has to read the phase the CALLER read out of `prev`, not a ref
  // written from an effect, for the same reason `onDeviceError` does: a refused
  // websocket does not emit once. The SDK retries the handshake and re-emits
  // for as long as the token stays dead, and the first of those to arrive after
  // the poll ended the call would otherwise repaint the ended screen red.
  if (OVER.has(phase)) return "ignore";
  if (!farEndIsUp(phase, direction)) return "failed";
  return code === GATEWAY_HANGUP ? "ended" : "dropped";
}

/** The `CallState.error` string for an error that the window has to show. */
export function callErrorTag(verdict: "dropped" | "failed", code: number | undefined): string {
  if (!code) return verdict === "dropped" ? "call_dropped" : "call_error";
  return `${verdict === "dropped" ? "dropped" : "twilio"}_${code}`;
}

/** The prefixes that carry a Twilio code, longest match first is not needed - they are distinct. */
const CODE_PREFIXES = ["twilio_", "dropped_"] as const;

/**
 * The number out of a `twilio_<code>` / `dropped_<code>` error, or "" for
 * anything else.
 *
 * Worth surfacing rather than swallowing: every Twilio failure shares one
 * message, and the code is the only part that says which one it is. 31002 and
 * 20101 both read as "the call was refused", but one is a dead TwiML app and the
 * other is a credential the region cannot verify, and without the number in
 * front of them nobody can tell those apart from the call window.
 */
export function twilioCode(error: string | null | undefined): string {
  if (!error) return "";
  const prefix = CODE_PREFIXES.find((p) => error.startsWith(p));
  return prefix ? error.slice(prefix.length) : "";
}

/**
 * The refusals where nothing was dialled and dialling again changes nothing.
 *
 * Two readers, one list, and that is the point of writing it down: the ended
 * screen hides its redial button on these, and the status line above it calls
 * them "not placed" rather than "failed". A number on the do-not-call list is
 * the clearest case - the register is the whole reason the call did not go out,
 * so a button offering to try again is the app arguing with its own rule.
 */
const NOT_PLACED: ReadonlySet<string> = new Set([
  "suppressed",
  "invalid_number",
  "unreachable_number",
  // The three ways "call from my mobile" is refused. All of them happen before
  // anything is dialled - two are configuration and the third is our own leg
  // failing - so a redial button offering to try the same thing again would be
  // the app suggesting the operator repeat themselves.
  "mobile_no_mobile",
  "mobile_not_conference",
  "mobile_leg_failed",
]);

/**
 * Why taking an AI call over in this window did not work (ring R6d).
 *
 * None of these is about the telephony settings: the AI's call was already
 * going, and in every case it is still the AI's. Four are the server saying no
 * to the press (a colleague is on the way, the opening is still playing, the
 * call is over, or it may not be taken over at all); `takeover_elsewhere` is
 * this operator's own attempt already running in another tab or on their
 * mobile; `takeover_interrupted` is this browser's leg dropping on its way in,
 * and `takeover_failed` anything else.
 */
const TAKEOVER_ERRORS: ReadonlySet<string> = new Set([
  "takeover_taken",
  "takeover_elsewhere",
  "takeover_opening",
  "takeover_gone",
  "takeover_refused",
  "takeover_interrupted",
  "takeover_failed",
]);

/** Was the call refused before anything was dialled? See `NOT_PLACED`. */
export function callWasNotPlaced(error: string | null | undefined): boolean {
  return NOT_PLACED.has(errorKey(error));
}

/**
 * The SHORT line for the status pill, as an i18n key.
 *
 * The pill is a pill: a word or three beside a dot, the same shape that says
 * "Live" and "Ringing" a second earlier. It used to carry the whole
 * explanation, which for the do-not-call refusal is two sentences - so the
 * pill grew into a red paragraph with a status dot stuck to the front of it,
 * and the one line that was supposed to be readable at a glance became the
 * hardest thing on the screen to read. The explanation moved to `CallError`
 * below the clock, where a paragraph is allowed to be one.
 */
export function errorStatusKey(error: string | null | undefined): string {
  const key = errorKey(error);
  if (key === "dropped") return "calls.dial.stDropped";
  if (TAKEOVER_ERRORS.has(key)) return "calls.dial.stTakeoverFailed";
  return NOT_PLACED.has(key) ? "calls.dial.stNotPlaced" : "calls.dial.stFailed";
}

/**
 * The Voice SDK codes this app can say something USEFUL about, when the call
 * never got up.
 *
 * Everything else keeps the generic sentence, which is the right answer for a
 * code nobody here has met: naming a number and pointing at the settings page is
 * more honest than a confident guess.
 *
 * ⚠ 20101 AND 20104 MUST NOT SEND ANYBODY TO THE SETTINGS PAGE. The app already
 * repairs both by itself - `device/useVoiceDevice.ts` calls `renew()` on exactly
 * these two codes and `device/refresh.ts` retries on a ladder. The generic
 * string tells the operator to go and check credentials that are fine, for a
 * problem that is over by the time they get there.
 *
 * ⚠ 31005 STAYS DELIBERATELY VAGUE. It is the SDK's catch-all (see
 * `GATEWAY_HANGUP` above): with improved signalling precision off, every gateway
 * code Twilio has no class for arrives as this one with the real number tucked
 * inside `originalError`. A confident sentence here would be a diagnosis of a
 * number we cannot read. Note also that this is the ONLY way 31005 reaches a
 * message at all - once the far end is up, `classifyCallError` turns it into an
 * ordinary ended call and no error is drawn.
 *
 * 31401 has no entry of its own on purpose: it is Twilio's name for the same
 * situation the browser reports as `NotAllowedError`, which already has a
 * sentence AND an icon. Two names, one microphone.
 */
const FAILED_CODES: Readonly<Record<string, string>> = {
  "20101": "session_expired",
  "20104": "session_expired",
  "31009": "no_transport",
  "31401": "mic_denied",
  "31003": "network_blocked",
  "53000": "signalling_lost",
  "31005": "gateway_refused",
};

/**
 * The same codes when the line died UNDER a live conversation.
 *
 * One string for all three, and that is a decision rather than laziness: from
 * the operator's chair a lost transport, a failed ICE path and a dead
 * signalling socket are the same event - the call was going, and then it was
 * not. The number still travels in `{code}` for anybody who needs to tell them
 * apart afterwards.
 */
const DROPPED_CODES: Readonly<Record<string, string>> = {
  "31009": "link_lost",
  "31003": "link_lost",
  "53000": "link_lost",
};

/**
 * The i18n suffix for the MESSAGE, which knows about codes; `errorKey` below
 * does not and must not learn.
 *
 * ⚠ WHY THIS IS A SECOND FUNCTION RATHER THAN A WIDER `errorKey`. Two callers
 * compare `errorKey`'s output to literals - `errorStatusKey` tests it against
 * "dropped" and looks it up in `NOT_PLACED`, and `callWasNotPlaced` does the
 * same. Teach `errorKey` to return "link_lost" and the status pill silently
 * falls back to "Call failed" on every dropped call: a caption regression with
 * no error anywhere to find it by. The coarse verdict and the exact wording are
 * two questions, so they are two functions.
 */
export function errorMessageKey(error: string | null | undefined): string {
  const code = twilioCode(error);
  if (code) {
    const table = error?.startsWith("dropped_") ? DROPPED_CODES : FAILED_CODES;
    const known = table[code];
    if (known) return known;
  }
  return errorKey(error);
}

/** Machine error codes to i18n suffixes; unknown codes share one message. */
export function errorKey(error: string | null | undefined): string {
  switch (error) {
    case "invalid_number":
    case "unknown_caller_id":
    case "telephony_unconfigured":
    case "mic_denied":
    // Twilio was asked about the number before it was dialled and answered
    // that it is not a real one. A different sentence from `invalid_number`,
    // which is about the shape of what was typed.
    case "unreachable_number":
    // The do-not-call list refused this number. Not a fault, and the one
    // refusal an operator must not read as "try again".
    case "suppressed":
    // "Call from my mobile" could not be honoured. Three sentences rather than
    // one, because they send the operator to three different places: their own
    // number is missing, the account is on the legacy bridge, or Twilio would
    // not ring the phone.
    case "mobile_no_mobile":
    case "mobile_not_conference":
    case "mobile_leg_failed":
    // Taking an AI call over did not work: each has its own sentence, and none
    // of them points at the settings. See `TAKEOVER_ERRORS`.
    case "takeover_taken":
    case "takeover_elsewhere":
    case "takeover_opening":
    case "takeover_gone":
    case "takeover_refused":
    case "takeover_interrupted":
    case "takeover_failed":
      return error;
    case "api_error":
    case "connect_failed":
    case "device_error":
    case "call_error":
      return "connect_failed";
    case "call_dropped":
      return "dropped";
    default:
      if (error?.startsWith("dropped_")) return "dropped";
      return error?.startsWith("twilio_") ? "twilio" : "connect_failed";
  }
}

/**
 * The `CallState.error` for a call that could not be started: the create request
 * refused it, the microphone was refused, or something else broke on the way to
 * `device.connect`.
 *
 * Moved out of `CallProvider.tsx`'s `startCall` unchanged, so the one place that
 * names a failed start can be tested without a browser.
 */
export function startCallError(err: unknown): string {
  if (err instanceof ApiError) {
    // 422 covers several different mistakes and each gets its own
    // sentence. A list rather than a chain of ternaries because the
    // list grows: today it is the LINE chosen to dial from, and a
    // number Twilio says does not exist. Anything not named here falls
    // back to the regex's own answer, which is the oldest meaning of
    // 422 on this route.
    //
    // ⚠ A code added on the server and not here does not fail loudly -
    // it silently becomes "this does not look like a dialable phone
    // number", which for a valid landline is simply untrue. It also
    // needs an arm in `callError.ts` `errorKey`, whose default is
    // "check the telephony settings" - the worst possible advice for a
    // number that is dead.
    // The three mobile refusals are here rather than in the 409 arm
    // below on purpose: that arm is spoken for by the do-not-call list,
    // and a second meaning on one status code is how an operator ends
    // up told a number is suppressed when their own phone is missing.
    const OWN_SENTENCE = [
      "unknown_caller_id",
      "unreachable_number",
      "mobile_no_mobile",
      "mobile_not_conference",
      "mobile_leg_failed",
    ];
    const unprocessable = OWN_SENTENCE.includes(err.message)
      ? err.message
      : "invalid_number";
    return err.status === 503
      ? "telephony_unconfigured"
      : // The number is on the do-not-call list. Its own sentence,
        // because the generic one reads as a network fault and the
        // operator would try again.
        err.status === 409
        ? "suppressed"
        : err.status === 422
          ? unprocessable
          : "api_error";
  }
  if (err instanceof DOMException && err.name === "NotAllowedError") return "mic_denied";
  return "connect_failed";
}
